import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { LoadingService } from '../../services/loading.service';
import { TrainingAnonymousService } from '../../services/training-anonymous.service';
import { TrainingAnonymousModel } from '../../models/training/training-anonymous.model';
import { HttpStatusCode } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EventParticipationQuestionModel } from 'src/app/models/event-participations/event-participation-question.model';
import { AnswerTrainingQuestionsDialogComponent } from '../answer-training-questions-dialog/answer-training-questions-dialog.component';
import { TrainingAnonymousStatusModel } from 'src/app/models/training/training-anonymous-status.model';
import { AcceptTrainingWithInvitationModel } from 'src/app/models/training/accept-training-anonymous.model';

@Component({
  selector: 'app-token-accept-flow',
  standalone: false,
  templateUrl: './token-accept-flow.component.html',
  styleUrl: './token-accept-flow.component.scss',
})
export class TokenAcceptFlowComponent implements OnInit {
  @ViewChild(AnswerTrainingQuestionsDialogComponent, { static: false })
  answerQuestionsDialogComponent?: AnswerTrainingQuestionsDialogComponent;

  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  @Input() trainingKey!: string;
  @Input() training: TrainingAnonymousModel | null = null;
  @Input() channelToken: string | null = null;

  @Input() questions: EventParticipationQuestionModel[] | null = null;
  @Input() remainingParticipantSlots: number | null = null;

  @Input() accessKey!: string;
  @Output() accessKeyChange = new EventEmitter<string | null>();

  @Output() onInviteAccepted = new EventEmitter();
  @Output() onAddedToQueue = new EventEmitter();

  showTrainingWaitingListModal: boolean = false;

  isTermsAccepted: boolean | null | undefined = false;
  isShowForm: boolean = false;
  isFormSubmitted: boolean = false;
  isAddedToQueue: boolean = false;

  formData: AcceptTrainingWithInvitationModel = {
    accessToken: '',
    channelToken: null,
    answers: null
  };

  constructor(
    private trainingAnonymousService: TrainingAnonymousService,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if(this.channelToken) {
      this.formData.channelToken = this.channelToken;
    }

    this.formData.accessToken = this.accessKey;

    this.resolveFlow();
  }

  private resolveFlow(): void {
    const hasAccessKey = !!this.accessKey;
    const hasQuestions = this.questions && this.questions.length;

    if (hasAccessKey && !hasQuestions) {
      this.acceptInvitation();
      return;
    }

    this.showForm();
  }

  isFormValid(): boolean {
    let isValid = true;

    if(this.answerQuestionsDialogComponent){
      const areQuestionsValid = this.answerQuestionsDialogComponent.validate();
      if(!areQuestionsValid) isValid = false;
    }

    return isValid;
  }
  
  onSubmit(): void {
    if (this.training?.status === TrainingAnonymousStatusModel.BookedUp) {
      this.addToQueue();
    } else {
      this.acceptInvitation();
    }
  }

  addToQueue() {
    const isValid = this.isFormValid();

    if (!isValid) {
      return;
    }

    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.showTrainingWaitingListModal = true;
  }

  onAddToQueue() {
    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.loadingService.load(
      this.trainingAnonymousService.enqueueTrainingWithInvitation(
        this.trainingKey,
        this.formData
      )
    ).subscribe({
       next: (key) => {
          if (key) {
            this.accessKey = key;
            this.accessKeyChange.emit(this.accessKey);
          }
          this.toastrService.success(
            this.translateService.instant('TRAINING-WAITING-LIST.ADDED-TO-QUEUE-SUCCESSFULLY')
          );

          this.isAddedToQueue = true;
          this.isFormSubmitted = true;
          this.showTrainingWaitingListModal = false;
          this.onAddedToQueue.emit();
        },
        error: (error) => {
          if (error.status === HttpStatusCode.Conflict) {
            this.toastrService.error(
              this.translateService.instant(
                "MY-INVITATION.INCORRECT-TOKEN-PROVIDED"
              )
            );

            this.navigateToInvitationBase();
          } else {
            this.toastrService.error(
              this.translateService.instant("COMMON.AN-ERROR-HAS-OCCURRED")
            );
          }
        },
    });
  }

  onAddToQueueClose() {
    this.showTrainingWaitingListModal = false;
  }

  acceptInvitation() {
    const isValid = this.isFormValid();

    if (!isValid) {
      return;
    }

    if(this.answerQuestionsDialogComponent){
      this.formData.answers = this.answerQuestionsDialogComponent.answers;
    }

    this.loadingService
      .load(
        this.trainingAnonymousService.acceptTrainingWithInvitation(
          this.trainingKey,
          this.formData
        )
      )
      .subscribe({
        next: (key) => {
          if (key) {
            this.accessKey = key;
            this.accessKeyChange.emit(this.accessKey);
          }
          this.toastrService.success('Invitation accepted successfully');
          this.onInviteAccepted.emit();
          this.isFormSubmitted = true;
        },
        error: (error) => {
          if (error.status === HttpStatusCode.Conflict) {
            const code = this.parseApiError(error.error)?.code;

            if (code === 'TRAINING_FULL') {
              this.training!.status = TrainingAnonymousStatusModel.BookedUp;
              this.showTrainingWaitingListModal = true;
              return;
            }

            if (code === 'TOKEN_NOT_FOUND') {
              this.toastrService.error(
                this.translateService.instant(
                  "MY-INVITATION.INCORRECT-TOKEN-PROVIDED"
                )
              );

              this.navigateToInvitationBase();
              return;
            }
          } else {
            this.toastrService.error(
              this.translateService.instant("COMMON.AN-ERROR-HAS-OCCURRED")
            );
          }
        },
      });
  }

  private parseApiError(error: any): any {
    if (typeof error === 'string') {
      try {
        return JSON.parse(error);
      } catch {
        return {};
      }
    }
    return error;
  }

  showForm() {
    this.isShowForm = true;
  }

  closeModal() {
    if (!this.training?.key) return;

    this.showModal = false;
    this.showModalChange.emit(this.showModal);

    if (this.accessKey) {
      this.router.navigate([`/my-invitation/${this.training.key}/${this.accessKey}`]);
    }
  }

  onPopupHiding() {
    console.log('On popup hide event!');
  }

  toggleCheckbox(checkbox: any) {
    checkbox.instance.option('value', !this.isTermsAccepted);
  }

  private navigateToInvitationBase(): void {
    if (!this.training?.key) return;

    const queryParams: any = {};

    if (this.channelToken) {
      queryParams.channel = this.channelToken;
    }

    this.router.navigate(
      [`/my-invitation/${this.training.key}`],
      { queryParams }
    );
  }
}
